import i18next, { type i18n as I18n, type Module } from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import { IntlMessageFormat } from "intl-messageformat";
import { catalogs, errorMessage, negotiateLocale } from "@agentsaas/i18n";
import { SUPPORTED_LOCALES, ApiError, type Locale } from "@agentsaas/protocol";
import pending from "../i18n.pending.json";

/**
 * i18next + ICU MessageFormat。翻译目录来自 @agentsaas/i18n；尚未合入的新 key 暂放 src/i18n.pending.json。
 * key 形如 "common.save"（扁平），因此关闭 keySeparator / nsSeparator。
 * i18next-icu 的 peer 只支持 intl-messageformat < 12，与 @agentsaas/i18n 不一致，这里用 12 自己实现格式化插件。
 */

const pendingCatalogs = pending as Record<Locale, Record<string, string>>;

export function mergedCatalog(locale: Locale): Record<string, string> {
  return { ...pendingCatalogs[locale], ...catalogs[locale] };
}

const fmtCache = new Map<string, IntlMessageFormat>();

export const icuFormat = {
  type: "i18nFormat",
  init() {},
  parse(res: string, options: Record<string, unknown>, lng: string, _ns: string, key: string) {
    const ck = `${lng}\u0000${key}\u0000${res}`;
    let f = fmtCache.get(ck);
    if (!f) {
      try {
        f = new IntlMessageFormat(res, lng);
      } catch {
        return res;
      }
      fmtCache.set(ck, f);
    }
    try {
      return String(f.format(options as Record<string, string | number>));
    } catch {
      return res;
    }
  },
  addLookupKeys(finalKeys: string[]) {
    return finalKeys;
  },
} as unknown as Module;

const LOCALE_KEY = "as.locale";

export function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(LOCALE_KEY);
    if (saved && (SUPPORTED_LOCALES as readonly string[]).includes(saved)) return saved as Locale;
  } catch {
    /* 隐私模式 */
  }
  return negotiateLocale(typeof navigator === "undefined" ? null : navigator.languages?.join(",") || navigator.language);
}

export function createI18n(lng: Locale = initialLocale()): I18n {
  const inst = i18next.createInstance();
  void inst
    .use(icuFormat)
    .use(initReactI18next)
    .init({
      lng,
      fallbackLng: "en",
      supportedLngs: [...SUPPORTED_LOCALES],
      resources: Object.fromEntries(SUPPORTED_LOCALES.map((l) => [l, { translation: mergedCatalog(l) }])),
      keySeparator: false,
      nsSeparator: false,
      interpolation: { escapeValue: false },
      returnNull: false,
      initAsync: false,
    });
  inst.on("languageChanged", (l) => {
    try {
      localStorage.setItem(LOCALE_KEY, l);
    } catch {
      /* ignore */
    }
    if (typeof document !== "undefined") document.documentElement.lang = l;
  });
  if (typeof document !== "undefined") document.documentElement.lang = lng;
  return inst;
}

export function currentLocale(i: I18n): Locale {
  const l = i.language;
  return (SUPPORTED_LOCALES as readonly string[]).includes(l) ? (l as Locale) : "en";
}

/** 组件里用：t 与当前 locale */
export function useT() {
  const { t, i18n } = useTranslation();
  return { t: t as (key: string, params?: Record<string, unknown>) => string, locale: currentLocale(i18n), i18n };
}

/** 统一的错误显示：errorMessage(locale, err) */
export function useErrorText() {
  const { locale } = useT();
  return (err: unknown) => errorMessage(locale, err instanceof ApiError ? err : { code: "common.internal" });
}
